import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { firestore } from '../firebase/config';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

function EmployeeDetails() {
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        const empDoc = await getDoc(doc(firestore, 'employees', id));
        if (empDoc.exists()) {
          setEmployee({ id: empDoc.id, ...empDoc.data() });
        } else {
          navigate('/admin');
        }
      } catch (error) {
        navigate('/admin');
      }
      setLoading(false);
    };
    fetchEmployee();
  }, [id, navigate]);

  useEffect(() => {
    const fetchAttendance = async () => {
      if (!employee) return;
      setAttendanceLoading(true);
      try {
        // Try multiple possible UID fields
        const possibleUids = [
          employee.uid,           // Primary UID field
          employee.id,            // Document ID
          employee.userUid,       // Alternative UID field
          employee.authUid        // Another possible UID field
        ].filter(Boolean); // Remove undefined/null values
        
        console.log('EmployeeDetails: Employee data:', employee);
        console.log('EmployeeDetails: Possible UIDs:', possibleUids);
        
        // Try to find the correct UID from the users collection
        let correctUid = null;
        try {
          // Search for user by contact number in users collection
          const usersQuery = query(collection(firestore, 'users'), where('contactNumber', '==', employee.contactNumber));
          const usersSnapshot = await getDocs(usersQuery);
          if (!usersSnapshot.empty) {
            const userDoc = usersSnapshot.docs[0];
            correctUid = userDoc.data().uid;
            console.log('EmployeeDetails: Found UID from users collection:', correctUid);
          }
        } catch (err) {
          console.log('EmployeeDetails: Error searching users collection:', err);
        }
        
        // Add the correct UID to the list if found
        if (correctUid && !possibleUids.includes(correctUid)) {
          possibleUids.unshift(correctUid); // Add to beginning of array
          console.log('EmployeeDetails: Added correct UID to possible UIDs:', correctUid);
        }
        
        // Try each possible UID until we find attendance records
        let records = [];
        for (const uid of possibleUids) {
          console.log('EmployeeDetails: Trying UID:', uid);
          const q = query(collection(firestore, 'employeeAttendance'), where('uid', '==', uid));
          const querySnapshot = await getDocs(q);
          const foundRecords = querySnapshot.docs.map(doc => doc.data());
          console.log('EmployeeDetails: Found records for UID', uid, ':', foundRecords.length);
          
          if (foundRecords.length > 0) {
            records = foundRecords;
            console.log('EmployeeDetails: Using UID:', uid, 'with', records.length, 'records');
            break;
          }
        }
        
        console.log('EmployeeDetails: Final attendance records:', records.length, records);
        setAttendance(records);
      } catch (err) {
        console.error('Error fetching attendance:', err);
        setAttendance([]);
      }
      setAttendanceLoading(false);
    };
    fetchAttendance();
  }, [employee]);

  // Attendance summary
  const totalPresent = attendance.filter(a => a.checkIn && a.checkOut).length;
  const totalAbsent = attendance.filter(a => !a.checkIn).length;
  const totalCheckedIn = attendance.filter(a => a.checkIn && !a.checkOut).length;
  const allDays = attendance.length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-xl text-gray-600">Employee not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-10">
      <div className="max-w-2xl mx-auto px-4">
        {/* Back Button */}
        <div className="mb-6">
          <div className="uiverse-box">
          <button 
            onClick={() => {
              const params = new URLSearchParams(location.search);
              if (params.get('tab') === 'members') {
                navigate('/admin?tab=members');
              } else {
                navigate('/admin');
              }
            }}
            className="flex items-center px-0 py-0 bg-transparent text-black rounded-full font-bold text-base gap-2 focus:outline-none border-none shadow-none"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </button>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8 p-8 flex flex-col items-center">
          {/* Profile Avatar */}
          <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mb-4 shadow">
            <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-1">{employee.fullName}</h1>
          <p className="text-lg text-blue-600 mb-2 font-semibold">{employee.position}</p>
          <div className="w-full mt-6 space-y-4">
            <div className="flex items-center bg-blue-50 rounded-lg p-4">
              <svg className="w-6 h-6 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0z" />
              </svg>
              <div>
                <p className="text-sm text-gray-600">Full Name</p>
                <p className="font-medium text-gray-800">{employee.fullName}</p>
              </div>
            </div>
            <div className="flex items-center bg-green-50 rounded-lg p-4">
              <svg className="w-6 h-6 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 21V7a2 2 0 00-2-2H6a2 2 0 00-2 2v14" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 3v4M8 3v4" />
              </svg>
              <div>
                <p className="text-sm text-gray-600">Position</p>
                <p className="font-medium text-gray-800">{employee.position}</p>
              </div>
            </div>
            <div className="flex items-center bg-yellow-50 rounded-lg p-4">
              <svg className="w-6 h-6 text-yellow-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14v7" />
              </svg>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium text-gray-800">{employee.email}</p>
              </div>
            </div>
            <div className="flex items-center bg-purple-50 rounded-lg p-4">
              <svg className="w-6 h-6 text-purple-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <div>
                <p className="text-sm text-gray-600">Contact Number</p>
                <p className="font-medium text-gray-800">{employee.contactNumber}</p>
              </div>
            </div>
            <div className="flex items-center bg-pink-50 rounded-lg p-4">
              <svg className="w-6 h-6 text-pink-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <div>
                <p className="text-sm text-gray-600">CNIC Number</p>
                <p className="font-medium text-gray-800">{employee.cnicNo}</p>
              </div>
            </div>
            <div className="flex items-center bg-indigo-50 rounded-lg p-4">
              <svg className="w-6 h-6 text-indigo-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <div>
                <p className="text-sm text-gray-600">Salary</p>
                <p className="font-medium text-gray-800">{employee.salary}</p>
              </div>
            </div>
            <div className="flex items-center bg-green-100 rounded-lg p-4">
              <svg className="w-6 h-6 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <div>
                <p className="text-sm text-gray-600">Paid Salary</p>
                <p className="font-medium text-gray-800">{employee.paidSalary}</p>
              </div>
            </div>
            <div className="flex items-center bg-red-100 rounded-lg p-4">
              <svg className="w-6 h-6 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <div>
                <p className="text-sm text-gray-600">Remaining Salary</p>
                <p className="font-medium text-gray-800">{employee.remainingSalary}</p>
              </div>
            </div>
            <div className="flex items-center bg-gray-50 rounded-lg p-4">
              <svg className="w-6 h-6 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <div>
                <p className="text-sm text-gray-600">Employee ID</p>
                <p className="font-medium text-gray-800">{employee.id}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8 p-8 mt-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
            <svg className="w-6 h-6 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Attendance Tracking
          </h2>
          {attendanceLoading ? (
            <div className="text-center py-8 text-gray-500">Loading attendance...</div>
          ) : (
            <>
              <div className="flex gap-6 mb-4">
                <span className="text-green-600 font-semibold">Total Present: {totalPresent}</span>
                <span className="text-red-600 font-semibold">Total Absent: {totalAbsent}</span>
                <span className="text-yellow-600 font-semibold">Checked In: {totalCheckedIn}</span>
                <span className="text-gray-700 font-semibold">All Days: {allDays}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-4 py-2 border">Date</th>
                      <th className="px-4 py-2 border">Check In</th>
                      <th className="px-4 py-2 border">Check Out</th>
                      <th className="px-4 py-2 border">Status</th>
                      <th className="px-4 py-2 border">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendance.length === 0 ? (
                      <tr><td colSpan={5} className="text-center py-4 text-gray-400">No attendance records found.</td></tr>
                    ) : (
                      attendance.sort((a, b) => (a.date < b.date ? 1 : -1)).map((a, i) => {
                        let status = 'Absent';
                        if (a.checkIn && a.checkOut) status = 'Present';
                        else if (a.checkIn && !a.checkOut) status = 'Checked In';
                        
                        // Calculate duration
                        let duration = '--';
                        function parseTime(timeStr) {
                          if (!timeStr) return null;
                          // Try 24-hour format first
                          let date = new Date(`1970-01-01T${timeStr}`);
                          if (!isNaN(date.getTime())) return date;
                          // Try 12-hour format (with AM/PM)
                          const match = timeStr.match(/(\d{1,2}):(\d{2}):(\d{2}) ?([APap][Mm])?/);
                          if (match) {
                            let [_, h, m, s, ampm] = match;
                            h = parseInt(h, 10);
                            m = parseInt(m, 10);
                            s = parseInt(s, 10);
                            if (ampm) {
                              if (ampm.toLowerCase() === 'pm' && h !== 12) h += 12;
                              if (ampm.toLowerCase() === 'am' && h === 12) h = 0;
                            }
                            return new Date(1970, 0, 1, h, m, s);
                          }
                          return null;
                        }
                        
                        if (a.checkIn && a.checkOut) {
                          const inDate = parseTime(a.checkIn);
                          const outDate = parseTime(a.checkOut);
                          if (inDate && outDate) {
                            if (outDate < inDate) outDate.setDate(outDate.getDate() + 1); // handle overnight
                            const diffMs = outDate - inDate;
                            const diffH = Math.floor(diffMs / (1000 * 60 * 60));
                            const diffM = Math.floor((diffMs / (1000 * 60)) % 60);
                            if (!isNaN(diffH) && !isNaN(diffM)) {
                              duration = `${diffH}h ${diffM}m`;
                            }
                          }
                        }
                        
                        return (
                          <tr key={i}>
                            <td className="border px-4 py-2">{a.date}</td>
                            <td className="border px-4 py-2">{a.checkIn || '-'}</td>
                            <td className="border px-4 py-2">{a.checkOut || '-'}</td>
                            <td className="border px-4 py-2">{status}</td>
                            <td className="border px-4 py-2 text-blue-700 font-semibold">{duration}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default EmployeeDetails; 